import { useState, useEffect } from "react";
import { X, Loader2, Save, Send, Trash2, CalendarClock, Plus } from "lucide-react";
import { toast } from "react-hot-toast";

type ReportType  = "capture_rates" | "pcc_performance";
type Frequency   = "daily" | "weekly";
type DateRange   = "today" | "yesterday" | "this_week" | "last_7_days";

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  today:       "Today",
  yesterday:   "Yesterday",
  this_week:   "This Week",
  last_7_days: "Last 7 Days",
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  capture_rates:   "Capture Rates",
  pcc_performance: "PCC Performance",
};

interface Region { id: string; name: string; }

type XlsxInterval = "daily" | "hourly";

interface Schedule {
  id: string;
  reportType: ReportType;
  frequency: Frequency;
  sendTime: string;
  dayOfWeek: number | null;
  dateRangeType: DateRange;
  regionIds: string[];
  recipientEmails: string[];
  includeXlsxAttachment: boolean;
  xlsxInterval: XlsxInterval;
  isActive: boolean;
  lastSentAt: string | null;
  nextScheduledAt: string | null;
}

interface ScheduleReportModalProps {
  reportType: ReportType;
  onClose: () => void;
}

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  };
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function ScheduleReportModal({ reportType, onClose }: ScheduleReportModalProps) {
  const [tab, setTab] = useState<"new" | "saved">("new");

  // Saved schedules
  const [schedules, setSchedules]           = useState<Schedule[]>([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  // Regions (CR only)
  const [regions, setRegions]               = useState<Region[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);

  // Edit mode — when editing a saved schedule
  const [editingId, setEditingId]           = useState<string | null>(null);

  // Form state
  const [frequency, setFrequency]           = useState<Frequency>("daily");
  const [sendTime, setSendTime]             = useState("08:00");
  const [dayOfWeek, setDayOfWeek]           = useState<number>(1); // Monday
  const [dateRangeType, setDateRangeType]   = useState<DateRange>("yesterday");
  const [selectedRegionIds, setSelectedRegionIds] = useState<string[]>([]);
  const [allRegions, setAllRegions]         = useState(true);
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput]         = useState("");
  const [includeXlsx, setIncludeXlsx]       = useState(true);
  const [xlsxInterval, setXlsxInterval]     = useState<XlsxInterval>("daily");
  const [isActive, setIsActive]             = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [sendingNow, setSendingNow]         = useState<string | null>(null);
  const [deletingId, setDeletingId]         = useState<string | null>(null);

  // Load saved schedules on mount
  useEffect(() => {
    fetchSchedules();
  }, []);

  // Load regions for CR
  useEffect(() => {
    if (reportType !== "capture_rates") return;
    setRegionsLoading(true);
    fetch("/api/v1/capture-rates/regions-list", {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) setRegions(d.data ?? []); })
      .catch(() => {})
      .finally(() => setRegionsLoading(false));
  }, [reportType]);

  const fetchSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const res = await fetch(
        `/api/v1/global-report-schedules?reportType=${reportType}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
      );
      const data = await res.json();
      if (data.success) setSchedules(data.data ?? []);
    } catch {
      // silent
    } finally {
      setLoadingSchedules(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFrequency("daily");
    setSendTime("08:00");
    setDayOfWeek(1);
    setDateRangeType("yesterday");
    setSelectedRegionIds([]);
    setAllRegions(true);
    setRecipientEmails([]);
    setEmailInput("");
    setIncludeXlsx(true);
    setXlsxInterval("daily");
    setIsActive(true);
  };

  const loadForEdit = (s: Schedule) => {
    setEditingId(s.id);
    setFrequency(s.frequency);
    setSendTime(s.sendTime);
    setDayOfWeek(s.dayOfWeek ?? 1);
    setDateRangeType(s.dateRangeType);
    setAllRegions(s.regionIds.length === 0);
    setSelectedRegionIds(s.regionIds);
    setRecipientEmails(s.recipientEmails);
    setEmailInput("");
    setIncludeXlsx(s.includeXlsxAttachment);
    setXlsxInterval(s.xlsxInterval ?? "daily");
    setIsActive(s.isActive);
    setTab("new");
  };

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Invalid email address");
      return;
    }
    if (recipientEmails.includes(email)) {
      toast.error("Email already added");
      return;
    }
    setRecipientEmails((prev) => [...prev, email]);
    setEmailInput("");
  };

  const removeEmail = (email: string) => {
    setRecipientEmails((prev) => prev.filter((e) => e !== email));
  };

  const toggleRegion = (id: string) => {
    setAllRegions(false);
    setSelectedRegionIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleAllRegionsToggle = () => {
    const next = !allRegions;
    setAllRegions(next);
    if (next) setSelectedRegionIds([]);
  };

  /** Returns the saved schedule id, or null on failure */
  const saveSchedule = async (): Promise<string | null> => {
    const finalEmails = [...recipientEmails];
    if (emailInput.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim())) {
        toast.error("Invalid email address");
        return null;
      }
      finalEmails.push(emailInput.trim());
    }
    if (finalEmails.length === 0) {
      toast.error("Add at least one recipient email");
      return null;
    }

    const payload = {
      reportType,
      frequency,
      sendTime,
      dayOfWeek: frequency === "weekly" ? dayOfWeek : null,
      dateRangeType,
      regionIds: allRegions ? [] : selectedRegionIds,
      recipientEmails: finalEmails,
      includeXlsxAttachment: includeXlsx,
      xlsxInterval,
      isActive,
    };

    const url = editingId
      ? `/api/v1/global-report-schedules/${editingId}`
      : "/api/v1/global-report-schedules";
    const method = editingId ? "PATCH" : "POST";

    const res = await fetch(url, {
      method,
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || "Failed to save");
    return data.data?.id as string;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSchedule();
      toast.success(editingId ? "Schedule updated" : "Schedule created");
      resetForm();
      await fetchSchedules();
      setTab("saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };


  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await fetch(`/api/v1/global-report-schedules/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      if (!res.ok) throw new Error("Failed to delete");
      toast.success("Schedule deleted");
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      if (editingId === id) resetForm();
    } catch {
      toast.error("Failed to delete schedule");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSendNow = async (id: string) => {
    setSendingNow(id);
    try {
      const res = await fetch(`/api/v1/global-report-schedules/${id}/send-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to send");
      toast.success("Report sent");
      await fetchSchedules();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSendingNow(null);
    }
  };

  const scheduleLabel = (s: Schedule) => {
    const freq = s.frequency === "weekly"
      ? `Weekly on ${DAY_NAMES[s.dayOfWeek ?? 1]}`
      : "Daily";
    return `${freq} at ${s.sendTime} · ${DATE_RANGE_LABELS[s.dateRangeType]} · ${s.recipientEmails.length} recipient${s.recipientEmails.length !== 1 ? "s" : ""}`;
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Schedule Report — {REPORT_TYPE_LABELS[reportType]}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 rounded p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setTab("new")}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === "new"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {editingId ? "Edit Schedule" : "New Schedule"}
          </button>
          <button
            onClick={() => setTab("saved")}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === "saved"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Saved Schedules{schedules.length > 0 ? ` (${schedules.length})` : ""}
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* ── NEW / EDIT tab ─────────────────────────────────────────── */}
          {tab === "new" && (
            <>
              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input
                      type="radio"
                      checked={frequency === "daily"}
                      onChange={() => setFrequency("daily")}
                      className="h-4 w-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700">Daily at</span>
                    <input
                      type="time"
                      value={sendTime}
                      onChange={(e) => setSendTime(e.target.value)}
                      disabled={frequency !== "daily"}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                    />
                  </label>
                  <label className="flex items-center gap-3 flex-wrap">
                    <input
                      type="radio"
                      checked={frequency === "weekly"}
                      onChange={() => setFrequency("weekly")}
                      className="h-4 w-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700">Weekly on</span>
                    <select
                      value={dayOfWeek}
                      onChange={(e) => setDayOfWeek(Number(e.target.value))}
                      disabled={frequency !== "weekly"}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                    >
                      {DAY_NAMES.map((d, i) => (
                        <option key={i} value={i}>{d}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-700">at</span>
                    <input
                      type="time"
                      value={sendTime}
                      onChange={(e) => setSendTime(e.target.value)}
                      disabled={frequency !== "weekly"}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                    />
                  </label>
                </div>
              </div>

              {/* Date range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                <select
                  value={dateRangeType}
                  onChange={(e) => setDateRangeType(e.target.value as DateRange)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {(Object.entries(DATE_RANGE_LABELS) as [DateRange, string][]).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>

              {/* XLSX attachment */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input
                    id="includeXlsx"
                    type="checkbox"
                    checked={includeXlsx}
                    onChange={(e) => setIncludeXlsx(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="includeXlsx" className="text-sm text-gray-700">Include XLSX attachment</label>
                </div>

                {/* Interval toggle — capture_rates + XLSX only */}
                {reportType === "capture_rates" && includeXlsx && (
                  <div className="ml-7 flex items-center gap-2">
                    <span className="text-sm text-gray-600">Breakdown by</span>
                    <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm">
                      <button
                        type="button"
                        onClick={() => setXlsxInterval("daily")}
                        className={`px-3 py-1 ${xlsxInterval === "daily" ? "bg-indigo-600 text-white font-medium" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                      >
                        Daily
                      </button>
                      <button
                        type="button"
                        onClick={() => setXlsxInterval("hourly")}
                        className={`px-3 py-1 border-l border-gray-300 ${xlsxInterval === "hourly" ? "bg-indigo-600 text-white font-medium" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                      >
                        Hourly
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Regions — capture_rates only */}
              {reportType === "capture_rates" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Regions</label>
                  {regionsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading regions…
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                        <input
                          type="checkbox"
                          checked={allRegions}
                          onChange={handleAllRegionsToggle}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        All Regions
                      </label>
                      {regions.map((r) => (
                        <label key={r.id} className="flex items-center gap-2 text-sm text-gray-700 pl-2">
                          <input
                            type="checkbox"
                            checked={allRegions || selectedRegionIds.includes(r.id)}
                            disabled={allRegions}
                            onChange={() => toggleRegion(r.id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                          />
                          {r.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipients</label>
                <div className="space-y-2">
                  {recipientEmails.map((email) => (
                    <div key={email} className="flex items-center gap-2">
                      <span className="flex-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
                        {email}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeEmail(email)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                      placeholder="email@example.com"
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={addEmail}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
              </div>
            </>
          )}

          {/* ── SAVED SCHEDULES tab ────────────────────────────────────── */}
          {tab === "saved" && (
            <>
              {loadingSchedules ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : schedules.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  No schedules yet.{" "}
                  <button
                    onClick={() => setTab("new")}
                    className="text-indigo-600 hover:underline font-medium"
                  >
                    Create one
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules.map((s) => (
                    <div
                      key={s.id}
                      className="rounded-lg border border-gray-200 p-4 hover:border-indigo-300 transition-colors cursor-pointer"
                      onClick={() => loadForEdit(s)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {scheduleLabel(s)}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            Last: {fmtDate(s.lastSentAt)} · Next: {fmtDate(s.nextScheduledAt)}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {s.recipientEmails.map((e) => (
                              <span key={e} className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                                {e}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${s.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {s.isActive ? "Active" : "Paused"}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleSendNow(s.id)}
                          disabled={sendingNow === s.id}
                          className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {sendingNow === s.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          Send Now
                        </button>
                        <button
                          onClick={() => handleDelete(s.id)}
                          disabled={deletingId === s.id}
                          className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingId === s.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {tab === "new" && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
            <button
              onClick={() => { resetForm(); if (schedules.length > 0) setTab("saved"); else onClose(); }}
              disabled={saving}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {editingId ? "Discard Changes" : "Cancel"}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : editingId ? "Update Schedule" : "Save Schedule"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
